'use client'

import { CreditCard, ExternalLink, Info, Loader2, ShieldAlert } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useUpgradePaymentMethodQuery } from '@/hooks/queries/billing-payment-method'
import type { BillingPlan, PaymentMethodSummary } from '@/lib/billing/types'
import { formatCredits } from '@/lib/billing/utils/credits'
import { formatCentsAsUsd, formatMonthlyPlanPrice } from '@/lib/billing/utils/format'
import { getPlanCreditsText } from './plan-features'

export interface StripePlanChange {
  plan: BillingPlan
  direction: 'upgrade' | 'downgrade'
}

interface StripePlanChangeDialogProps {
  change: StripePlanChange | null
  currentPlanName: string | null
  /** The current period end as the Plan card shows it, e.g. "Oct 24". */
  periodEndLabel: string | null
  /** When the current plan's payment credits expire, e.g. "Oct 25, 2026"; null when they do not. */
  currentPlanCreditsUntil: string | null
  submitting: boolean
  /** Stripe accepted the change, but the plan has not flipped within the wait budget. */
  syncTimedOut: boolean
  onConfirm: (plan: BillingPlan) => void
  onClose: () => void
  /** Opens the Stripe Customer Portal to add or swap the card. */
  onManageCard: () => void
}

/** Confirms an in-place Stripe plan change and states what happens to money and credits. */
export function StripePlanChangeDialog({
  change,
  currentPlanName,
  periodEndLabel,
  currentPlanCreditsUntil,
  submitting,
  syncTimedOut,
  onConfirm,
  onClose,
  onManageCard,
}: StripePlanChangeDialogProps) {
  const { data: paymentMethod } = useUpgradePaymentMethodQuery(change?.direction === 'upgrade' ? change.plan.id : null)

  return (
    <AlertDialog open={change !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <AlertDialogContent className={submitting ? 'cursor-wait' : ''}>
        {change && (
          <ChangeDetails
            change={change}
            currentPlanName={currentPlanName}
            periodEndLabel={periodEndLabel}
            currentPlanCreditsUntil={currentPlanCreditsUntil}
            submitting={submitting}
            syncTimedOut={syncTimedOut}
            paymentMethod={paymentMethod}
            onConfirm={onConfirm}
            onManageCard={onManageCard}
          />
        )}
      </AlertDialogContent>
    </AlertDialog>
  )
}

function ChangeDetails({
  change,
  currentPlanName,
  periodEndLabel,
  currentPlanCreditsUntil,
  submitting,
  syncTimedOut,
  paymentMethod,
  onConfirm,
  onManageCard,
}: Omit<StripePlanChangeDialogProps, 'change' | 'onClose'> & {
  change: StripePlanChange
  paymentMethod: PaymentMethodSummary | null | undefined
}) {
  const { plan: target, direction } = change
  const isUpgrade = direction === 'upgrade'
  const planCredits = getPlanCreditsText(target)

  return (
    <>
      <AlertDialogHeader className={`transition-opacity ${submitting ? 'opacity-50' : ''}`}>
        <AlertDialogTitle>{dialogTitle(change)}</AlertDialogTitle>
        <AlertDialogDescription>
          {dialogDescription(change, currentPlanName, periodEndLabel, keptCreditsText(currentPlanName, currentPlanCreditsUntil))}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <div className={`space-y-2 py-2 text-sm transition-opacity ${submitting ? 'opacity-50 pointer-events-none' : ''}`}>
        {isUpgrade && (
          <>
            <div className="flex justify-between gap-3">
              <span>Charge to your card now</span>
              <span className="font-medium">{formatCentsAsUsd(target.price_monthly_cents)}</span>
            </div>
            <SavedCardLine paymentMethod={paymentMethod} onManageCard={onManageCard} />
          </>
        )}
        {isUpgrade && planCredits && (
          <div className="flex justify-between gap-3">
            <span>Credits with this payment</span>
            <span className="font-medium text-right">{planCredits}</span>
          </div>
        )}
        {/* A switch to Free grants nothing: the tenant got Free's starting credits at registration. */}
        {target.price_monthly_cents > 0 && target.initial_balance_microusd > 0 && (
          <p className="pt-1 text-xs text-muted-foreground">
            <span>{`Moving to ${target.display_name} for the first time also adds ${formatCredits(target.initial_balance_microusd)} to start.`}</span>
          </p>
        )}
      </div>
      {(submitting || syncTimedOut) && (
        <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs text-foreground">
          {submitting ? (
            <>
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
              <span>Applying plan change — waiting for Stripe to confirm…</span>
            </>
          ) : (
            <>
              <Info className="h-3.5 w-3.5 shrink-0" />
              <span>Stripe accepted the change. The plan name may take another moment to refresh — you can close this dialog or refresh the page.</span>
            </>
          )}
        </div>
      )}
      <AlertDialogFooter>
        <AlertDialogCancel disabled={submitting}>{syncTimedOut ? 'Close' : 'Cancel'}</AlertDialogCancel>
        {!syncTimedOut && (
          <ConfirmAction
            change={change}
            paymentMethod={paymentMethod}
            submitting={submitting}
            onConfirm={onConfirm}
            onManageCard={onManageCard}
          />
        )}
      </AlertDialogFooter>
    </>
  )
}

function dialogTitle({ plan, direction }: StripePlanChange): string {
  if (direction === 'upgrade') return `Confirm upgrade to ${plan.display_name}`
  return plan.price_monthly_cents === 0
    ? `Switch to ${plan.display_name} at the end of the billing period`
    : `Confirm downgrade to ${plan.display_name}`
}

function dialogDescription(
  { plan, direction }: StripePlanChange,
  currentPlanName: string | null,
  periodEndLabel: string | null,
  keptCredits: string,
): string {
  if (direction === 'upgrade') {
    return `Your card is charged for ${plan.display_name} now, and a new billing period starts today. ${keptCredits}`
  }
  if (plan.price_monthly_cents === 0) {
    const current = currentPlanName ?? 'Your current plan'
    const until = periodEndLabel ?? 'the end of the current billing period'
    return `${current} stays active until ${until}, then your plan switches to ${plan.display_name}. ${keptCredits}`
  }
  const from = periodEndLabel ? `From ${periodEndLabel}` : 'From your next invoice'
  const credits = getPlanCreditsText(plan)
  return `Your plan changes now with no charge today. ${from} you pay ${formatMonthlyPlanPrice(plan.price_monthly_cents)}${credits ? ` and get ${credits}` : ''}. ${keptCredits}`
}

function keptCreditsText(currentPlanName: string | null, currentPlanCreditsUntil: string | null): string {
  if (currentPlanName && currentPlanCreditsUntil) return `Credits from ${currentPlanName} stay until ${currentPlanCreditsUntil}.`
  return 'Credits you already have stay.'
}

function SavedCardLine({
  paymentMethod,
  onManageCard,
}: {
  paymentMethod: PaymentMethodSummary | null | undefined
  onManageCard: () => void
}) {
  if (paymentMethod === undefined) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>Loading payment method…</span>
      </div>
    )
  }
  if (paymentMethod === null) {
    return (
      <div className="flex items-start gap-1.5 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-destructive">
        <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>No card on file. Add a card to continue with this upgrade.</span>
      </div>
    )
  }
  const expiry = `${String(paymentMethod.expMonth).padStart(2, '0')}/${String(paymentMethod.expYear).slice(-2)}`
  return (
    <div className={`flex flex-wrap items-center justify-between gap-2 ${paymentMethod.isExpired ? 'text-destructive' : 'text-muted-foreground'}`}>
      <span className="flex items-center gap-1.5">
        <CreditCard className="h-3.5 w-3.5" />
        <span className="capitalize">{paymentMethod.brand}</span>
        <span>{`•••• ${paymentMethod.last4}`}</span>
        <span>{paymentMethod.isExpired ? `(expired ${expiry})` : `(exp ${expiry})`}</span>
      </span>
      <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={onManageCard}>
        {paymentMethod.isExpired ? 'Update card' : 'Use a different card'}
      </Button>
    </div>
  )
}

/** Upgrades wait for the card they will charge and never charge an expired one; without a card, "Add card". */
function ConfirmAction({
  change: { plan, direction },
  paymentMethod,
  submitting,
  onConfirm,
  onManageCard,
}: {
  change: StripePlanChange
  paymentMethod: PaymentMethodSummary | null | undefined
  submitting: boolean
  onConfirm: (plan: BillingPlan) => void
  onManageCard: () => void
}) {
  const isUpgrade = direction === 'upgrade'
  if (isUpgrade && paymentMethod === null) {
    return (
      <AlertDialogAction onClick={onManageCard}>
        <span>Add card</span>
        <ExternalLink className="h-3.5 w-3.5 ml-1" />
      </AlertDialogAction>
    )
  }
  return (
    <AlertDialogAction
      // Radix closes the dialog on the primary action; the page closes it once the plan settles.
      onClick={(event) => { event.preventDefault(); onConfirm(plan) }}
      disabled={submitting || (isUpgrade && (!paymentMethod || paymentMethod.isExpired))}
    >
      {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
      <span>{isUpgrade ? 'Confirm and pay' : 'Confirm'}</span>
    </AlertDialogAction>
  )
}
